<script lang="ts" setup>
import ArrowIcon from "~/assets/icons/arrows/16/chevron-right.svg"
import HarbingerLogo from "~/assets/icons/logos/harbinger.svg?url";
import SotransLogo from "~/assets/icons/logos/sotrans.svg?url";
import HarbingerImg from "~/assets/static-img/catalog/harbinger.png"
import SotransImg from "~/assets/static-img/catalog/sotrans.png";
import CatalogMenuChild from "~/widgets/catalog/menu-components/CatalogMenuChild.vue";
import useCatalogStore from "~/shared/store/modules/catalog";
import getHeaderPosition from "~/composables/get-header-position";
import useMainStore from "~/shared/store";
import type { ICatalogCategory } from "~/shared/interfaces/catalog";

const props = defineProps<{
  isActive: boolean
}>()

const emits = defineEmits(['close'])

const mainStore = useMainStore()
const catalogStore = useCatalogStore()
const staticLinks = [
  {
    url: '/brands/harbinger',
    logo: HarbingerLogo,
    img: HarbingerImg
  },
  {
    url: '/brands/sotrans',
    logo: SotransLogo,
    img: SotransImg
  }
]
const menuTop = ref('')
const currentCategory = ref<ICatalogCategory>(catalogStore.catalogCategories[0]);
const columns = ref<ICatalogCategory[][]>([[], [], []])

watch(() => props.isActive, () => {
  if (props.isActive) {
    const headerPosition = getHeaderPosition()
    menuTop.value = `${headerPosition ? headerPosition.bottom - 1 : 0}px`
    mainStore.setBodyFixed(true)
  } else {
    mainStore.setBodyFixed(false)
  }
})

onBeforeUnmount(() => {
  mainStore.setBodyFixed(false)
})

function setCurrentCategory(uuid: string) {
  currentCategory.value = catalogStore.catalogCategories.find(el => el.uuid === uuid) as ICatalogCategory;
  separateMenuItems();
}

function separateMenuItems() {
  columns.value = [[], [], []]
  currentCategory.value?.childCategories.forEach((el, i) => columns.value[i % 3].push(el))
}

function closeMenu() {
  emits('close')
}

separateMenuItems()
</script>

<template>
  <Transition name="fade">
    <div
      class="catalog-menu"
      v-if="isActive"
      @click.self="closeMenu"
    >
      <div class="catalog-menu__container">
        <div class="container">
          <div class="catalog-menu__left">
            <div class="catalog-menu__left-static-links">
              <NuxtLink
                v-for="(link, index) in staticLinks"
                :key="`static_link_${index}`"
                :to="link.url"
                @click="closeMenu"
              >
                <img :src="link.logo" alt="">
                <img :src="link.img" alt="">
              </NuxtLink>
            </div>
            <div class="catalog-menu__left-separator"></div>
            <div class="catalog-menu__left-items">
              <template
                v-for="category in catalogStore.catalogCategories"
                :key="category.slug"
              >
                <NuxtLink
                  v-if="category.isShow"
                  :class="category.uuid === currentCategory.uuid && 'active'"
                  :to="`/catalog/${category.slug}`"
                  @mouseenter="setCurrentCategory(category.uuid)"
                  @click="closeMenu"
                >
                  <span>{{ category.name }}</span>
                  <ArrowIcon v-if="category.childCategories.length"/>
                </NuxtLink>
              </template>
            </div>
          </div>
          <div class="catalog-menu__right">
            <div class="catalog-menu__right-container" v-if="currentCategory.childCategories.length">
              <NuxtLink
                class="catalog-menu__right-title"
                :to="`/catalog/${currentCategory.slug}`"
                @click="closeMenu"
              >
                {{ currentCategory.name }}
              </NuxtLink>
              <div class="catalog-menu__right-items">
                <div
                  v-for="(column, index) in columns"
                  :key="`categories_column_${index}`"
                  class="catalog-menu__right-items-block"
                >
                  <template
                    v-for="childCategory in column"
                    :key="childCategory.uuid"
                  >
                    <CatalogMenuChild
                      v-if="childCategory.isShow"
                      :category="childCategory"
                      :parent-category="currentCategory"
                      @close="closeMenu"
                    />
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style lang="scss" scoped>
@-moz-document url-prefix() {
  .catalog-menu__right-container {
    scrollbar-color: $bg_page $control-content_tretiary;
    scrollbar-width: thin;
  }
}

.catalog-menu {
  position: fixed;
  top: v-bind(menuTop);
  left: 0;
  width: 100%;
  height: calc(100vh - v-bind(menuTop));
  background-color: $bg_modal;
  z-index: 99;
  transition: top .3s;

  @include md-breakpoint {
    display: none;
  }

  &__container {
    padding: 20px 0;
    background-color: $bg_page;
    max-height: 100%;
    min-height: calc(100vh - 180px);
    overflow: auto;

    .container {
      display: flex;
      gap: 49px;
    }
  }

  &__left {
    width: 241px;

    &-static-links {
      display: flex;
      flex-direction: column;
      gap: 8px;

      a {
        height: 56px;
        width: 100%;
        background-color: $control-bg_neutral;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-left: 8px;
        border-radius: 8px;
        overflow: hidden;

        &:hover:not(:active) {
          @include mf-breakpoint {
            background-color: $control-bg_neutral-hover;
          }
        }
      }

      img {
        mix-blend-mode: multiply;
      }
    }

    &-separator {
      margin: 12px 0;
      height: 1px;
      width: 100%;
      background-color: $bg_border;
    }

    &-items {
      a {
        padding: 12px 16px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        border-radius: 8px;
        gap: 16px;

        &.active {
          background-color: $control-bg_neutral;
        }

        svg {
          flex-shrink: 0;
        }
      }
    }
  }

  &__right {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    gap: 24px;

    &-container {
      max-height: calc(100vh - v-bind(menuTop) - 40px);
      height: 100%;
      overflow: auto;

      &::-webkit-scrollbar {
        width: 4px;
        background-color: $bg_page;
      }

      &::-webkit-scrollbar-thumb {
        border-radius: 5px;
        background: $content_secondary;
      }
    }

    &-title {
      @include font(24px, 28px, 600);
      &:hover:not(:active) {
        @include mf-breakpoint {
          color: $control-content_orange;
        }
      }
    }

    &-items {
      margin-top: 24px;
      max-height: calc(100% - v-bind(menuTop));
      display: grid;
      grid-template-columns: repeat(3, 1fr);

      &-block {
        display: flex;
        flex-direction: column;
        gap: 20px;
      }
    }
  }

  &__child {
    &>a {
      font-weight: 500;
    }

    &-items {
      display: flex;
      flex-direction: column;
      gap: 8px;
      margin-top: 8px;
      @include font(14px, 20px);
    }
  }
}
</style>
